import { useEffect, useState } from "react";

const cache = new Map();
const EMPTY = { bg: null, ratio: null };

const smallUrl = (src) =>
  src.includes("cdn.shopify.com") ? `${src}${src.includes("?") ? "&" : "?"}width=60` : src;

const median = (arr) => {
  const s = [...arr].sort((a, b) => a - b);
  return s[Math.floor(s.length / 2)];
};

// Reads a photo's backdrop colour (edge median) and aspect ratio so its frame can blend seamlessly.
export function useImageBg(src) {
  const [info, setInfo] = useState(() => (src && cache.get(src)) || EMPTY);

  useEffect(() => {
    if (!src) return undefined;
    if (cache.has(src)) {
      setInfo(cache.get(src));
      return undefined;
    }
    let live = true;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const { naturalWidth: w, naturalHeight: h } = img;
        const c = document.createElement("canvas");
        c.width = w;
        c.height = h;
        const ctx = c.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(img, 0, 0);
        const data = ctx.getImageData(0, 0, w, h).data;
        const ch = [[], [], []];
        const push = (x, y) => {
          const i = (y * w + x) * 4;
          ch[0].push(data[i]);
          ch[1].push(data[i + 1]);
          ch[2].push(data[i + 2]);
        };
        const tall = h / w > 1.04;
        const wide = h / w < 0.96;
        if (!tall) for (let x = 0; x < w; x += 1) { push(x, 0); push(x, h - 1); }
        if (!wide) for (let y = 1; y < h - 1; y += 1) { push(0, y); push(w - 1, y); }
        const next = { bg: `rgb(${ch.map(median).join(",")})`, ratio: h / w };
        cache.set(src, next);
        if (live) setInfo(next);
      } catch {
        /* cross-origin blocked: keep fallback colour */
      }
    };
    img.src = smallUrl(src);
    return () => {
      live = false;
    };
  }, [src]);

  return info;
}
